import type { LocalPhoto } from '../../lib/types';

interface MobileCardProps {
  photo: LocalPhoto;
  onRemove: () => void;
  /** Round 7: explicit mobile select mode (phones have no marquee/shift-click).
   *  In select mode the whole card toggles selection and the ✕ is hidden. */
  selectMode?: boolean;
  selected?: boolean;
  onToggle?: () => void;
}

/** Mobile 3-col square upload card (quality messaging lives in Step 2). */
export function MobileCard({
  photo,
  onRemove,
  selectMode = false,
  selected = false,
  onToggle,
}: MobileCardProps) {
  const uploading = photo.status === 'uploading' || photo.status === 'hashing';
  const error = photo.status === 'error';
  const selectable = selectMode && photo.status === 'ready';
  return (
    <div
      className={`mcard${error ? ' pcard--error' : ''}${selected ? ' mcard--selected' : ''}`}
      role={selectable ? 'checkbox' : undefined}
      aria-checked={selectable ? selected : undefined}
      aria-label={selectable ? `Select ${photo.filename}` : undefined}
      tabIndex={selectable ? 0 : undefined}
      onClick={selectable ? onToggle : undefined}
      onKeyDown={
        selectable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle?.();
              }
            }
          : undefined
      }
    >
      {photo.previewUrl && (
        <img className="mcard__img" src={photo.previewUrl} alt={photo.filename} loading="lazy" />
      )}
      {selectMode ? (
        photo.status === 'ready' && (
          <span className={`mcard__pick${selected ? ' mcard__pick--on' : ''}`} aria-hidden="true">
            {selected ? '✓' : ''}
          </span>
        )
      ) : (
        <>
          {photo.status === 'ready' && (
            <span className="mcard__uploaded" role="img" aria-label="Uploaded" title="Uploaded ✓">
              ✓
            </span>
          )}
          <button
            type="button"
            className="mcard__remove"
            onClick={onRemove}
            aria-label={`Remove ${photo.filename}`}
          >
            ✕
          </button>
        </>
      )}
      {error && <span className="mcard__overlay">Couldn’t read</span>}
      {uploading && (
        <div className="mcard__progress" aria-hidden="true">
          <div className="mcard__progress-fill" style={{ width: `${photo.progress}%` }} />
        </div>
      )}
    </div>
  );
}
